import {
  Button,
  Divider,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiShare2 } from "react-icons/fi";
import {
  type EventShareRowDto,
  type PersonDto,
  type SourcingEventV2Dto,
  useGetEventSharesQuery,
  useShareEventMutation,
  useUnshareEventMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { PersonSelector } from "../../../common/persons/PersonSelector";
import { Explanation } from "../../../common/support/Explanation/Explanation";

const personName = (p: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

export const ShareEventButton = ({ event, ownerOrgId }: { event: SourcingEventV2Dto; ownerOrgId: string }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { trigger, triggerSoft } = useApiError();
  const { me } = useLoggedInWithOrgContextState();
  const [selected, setSelected] = useState<PersonDto | null>(null);
  const [removingPersonId, setRemovingPersonId] = useState<string | null>(null);
  const { data, error: sharesError } = useGetEventSharesQuery({ eventId: event.id }, { skip: !isOpen });
  const [share, { isLoading: isSharing }] = useShareEventMutation();
  const [unshare] = useUnshareEventMutation();

  useEffect(() => {
    if (sharesError) triggerSoft(sharesError);
  }, [sharesError, triggerSoft]);

  const shareOperation = event.operations?.find((o) => o.operation === "ShareEvent");
  if (!shareOperation?.allowed) return null;

  const shares = data?.shares ?? [];

  const onShare = async () => {
    if (!selected) return;
    try {
      const shared = await share({ eventId: event.id, shareEventRequest: { personId: selected.id } }).unwrap();
      toast({ title: t("Shared with {{name}}", { name: personName(shared) }), status: "success", duration: 4000 });
      setSelected(null);
    } catch (e) {
      trigger(e);
    }
  };

  const onRemove = async (row: EventShareRowDto) => {
    setRemovingPersonId(row.personId);
    try {
      await unshare({ eventId: event.id, pathPersonId: row.personId }).unwrap();
    } catch (e) {
      trigger(e);
    } finally {
      setRemovingPersonId(null);
    }
  };

  return (
    <>
      <IconButton
        size="sm"
        variant="outline"
        aria-label={t("Share procurement")}
        icon={<Icon as={FiShare2} />}
        onClick={onOpen}
      />
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Share procurement")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <Text fontSize="sm" color="smMuted">
                {t("A colleague you share with can see exactly this procurement.")}
              </Text>
              <HStack align="flex-start">
                <Stack flex="1">
                  <PersonSelector
                    size="md"
                    organizationIds={[ownerOrgId]}
                    selectedPerson={selected}
                    onChange={setSelected}
                    personsToExclude={[...shares.map((s) => s.personId), me.id]}
                    placeholder={t("Share with a colleague") ?? ""}
                  />
                </Stack>
                <Button colorScheme="teal" onClick={onShare} isLoading={isSharing} isDisabled={!selected}>
                  {t("Share")}
                </Button>
              </HStack>
              <Divider />
              {data && shares.length === 0 && (
                <Text fontSize="sm" color="smMuted">
                  {t("Not shared with anyone yet.")}
                </Text>
              )}
              {shares.map((row) => (
                <HStack key={row.personId} justify="space-between" align="flex-start">
                  <Stack spacing="0">
                    <HStack>
                      <Text fontWeight="600">{personName(row)}</Text>
                      {row.dormant && (
                        <Explanation
                          text={t("The recipient no longer holds a role in the organization.")}
                          enabled
                        >
                          <Tag size="sm">{t("Dormant")}</Tag>
                        </Explanation>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="smMuted">
                      {row.email}
                    </Text>
                    <Text fontSize="xs" color="smMuted">
                      {t("Shared by {{name}}", { name: personName(row.sharedBy) })}
                    </Text>
                  </Stack>
                  <Explanation
                    text={row.revoke.reason ? t(row.revoke.reason.code) : ""}
                    enabled={!row.revoke.allowed}
                    shouldWrapChildren
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      isDisabled={!row.revoke.allowed}
                      isLoading={removingPersonId === row.personId}
                      onClick={() => onRemove(row)}
                    >
                      {t("Remove")}
                    </Button>
                  </Explanation>
                </HStack>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("Close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
